import { ComponentPropsWithoutRef, ReactNode, cloneElement, forwardRef, isValidElement, useState } from 'react'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'

import DotsSpinner from '@components/ui/dots-spinner'
import { cn } from '@lib/cn'

export const Root = DropdownMenuPrimitive.Root
export const Trigger = DropdownMenuPrimitive.Trigger
export const Portal = DropdownMenuPrimitive.Portal
export const Sub = DropdownMenuPrimitive.Sub
export const RadioGroup = DropdownMenuPrimitive.RadioGroup

const contentClass =
  'min-w-[200px] z-50 py-1.5 bg-o-card border border-o-line rounded-xl shadow-o-pane text-[13px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'

export const Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>>(
  ({ className, sideOffset = 8, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(contentClass, className)}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
)
Content.displayName = 'DropdownMenuContent'

export const SubContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      sideOffset={sideOffset}
      className={cn(contentClass, className)}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
SubContent.displayName = 'DropdownMenuSubContent'

export const Separator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('my-1.5 h-px bg-o-line', className)}
    {...props}
  />
))
Separator.displayName = 'DropdownMenuSeparator'

export const Label = ({ className, ...props }: ComponentPropsWithoutRef<'div'>) => (
  <div
    className={cn('px-3.5 py-1.5 text-xs text-o-muted', className)}
    {...props}
  />
)

export const itemClass =
  'relative flex items-center gap-2.5 px-3 py-2 mx-1 rounded-lg cursor-pointer select-none outline-hidden text-o-ink data-[highlighted]:bg-o-chip data-[disabled]:opacity-50 data-[disabled]:pointer-events-none'

interface ItemProps extends ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  icon?: ReactNode
  loading?: boolean
  destructive?: boolean
  shortcut?: string
}

export const Item = forwardRef<HTMLDivElement, ItemProps>(
  ({ className, icon, loading, destructive, shortcut, children, asChild, ...props }, ref) => {
    const renderContent = (label: ReactNode) => (
      <>
        {icon && <span className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-4 [&>svg]:fill-current">{icon}</span>}
        <span className="flex-1 truncate">{label}</span>
        {loading && <DotsSpinner />}
        {shortcut && <span className="ms-auto text-xs text-o-faint">{shortcut}</span>}
      </>
    )

    const itemClassName = cn(
      itemClass,
      destructive && 'text-o-danger data-[highlighted]:bg-o-danger-bg',
      className
    )

    if (asChild && isValidElement<{ children?: ReactNode }>(children)) {
      return (
        <DropdownMenuPrimitive.Item ref={ref} asChild className={itemClassName} {...props}>
          {cloneElement(children, undefined, renderContent(children.props.children))}
        </DropdownMenuPrimitive.Item>
      )
    }

    return (
      <DropdownMenuPrimitive.Item ref={ref} className={itemClassName} {...props}>
        {renderContent(children)}
      </DropdownMenuPrimitive.Item>
    )
  }
)
Item.displayName = 'DropdownMenuItem'

export const RadioItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(itemClass, 'data-[state=checked]:bg-o-chip', className)}
    {...props}
  />
))
RadioItem.displayName = 'DropdownMenuRadioItem'

export const SubTrigger = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & { icon?: ReactNode }
>(({ className, icon, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(itemClass, 'data-[state=open]:bg-o-chip', className)}
    {...props}
  >
    {icon && <span className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-4 [&>svg]:fill-current">{icon}</span>}
    <span className="flex-1 truncate">{children}</span>
  </DropdownMenuPrimitive.SubTrigger>
))
SubTrigger.displayName = 'DropdownMenuSubTrigger'

interface DeleteItemProps extends Omit<ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>, 'onSelect'> {
  onConfirm: () => void
  icon?: ReactNode
  loading?: boolean
  confirmLabel?: string
}

export const DeleteItem = forwardRef<HTMLDivElement, DeleteItemProps>(
  ({ className, icon, loading, onConfirm, confirmLabel = 'Confirm deletion', children, ...props }, ref) => {
    const [confirming, setConfirming] = useState(false)

    return (
      <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
          itemClass,
          'text-o-danger data-[highlighted]:bg-o-danger-bg',
          confirming && 'bg-o-red text-white data-[highlighted]:bg-o-red data-[highlighted]:opacity-90',
          className
        )}
        onSelect={(event) => {
          event.preventDefault()
          if (!confirming) {
            setConfirming(true)
            return
          }
          onConfirm()
        }}
        {...props}
      >
        {icon && <span className="flex size-4 shrink-0 items-center justify-center [&>svg]:size-4 [&>svg]:fill-current">{icon}</span>}
        <span className="flex-1 truncate">{confirming ? confirmLabel : children}</span>
        {loading && <DotsSpinner />}
      </DropdownMenuPrimitive.Item>
    )
  }
)
DeleteItem.displayName = 'DropdownMenuDeleteItem'
